// ============================================================================
// KLINISOL CRO - Navigation Bar Component (UPDATED)
// COLOR SCHEME: Slate Grey (#475569) & Magenta (#E91E63)
// ============================================================================

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, PowerOff, BookOpen, Users, ChevronDown } from 'lucide-react';

const Navbar = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isResourcesOpen, setIsResourcesOpen] = useState(false);
    const { isAuthenticated, user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const closeAllMenus = () => {
        setIsMenuOpen(false);
        setIsResourcesOpen(false);
    };

    return (
        <nav className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-md sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-6 py-4">
                <div className="flex items-center justify-between">
                    {/* Logo */}
                    <Link to="/" className="flex flex-col" onClick={closeAllMenus}>
                        <span className="text-2xl font-bold tracking-tight">
                            KLIN<span className="text-[#E91E63]">i</span>SOL
                        </span>
                        <span className="text-xs opacity-90 hidden sm:block">
                            Advancing Investigator-Initiated Research
                        </span>
                    </Link>

                    {/* Desktop Navigation */}
                    <ul className="hidden lg:flex items-center gap-6">
                        <li>
                            <Link to="/" className="text-sm font-medium hover:text-[#E91E63] transition-colors">
                                Home
                            </Link>
                        </li>
                        <li>
                            <Link to="/services" className="text-sm font-medium hover:text-[#E91E63] transition-colors">
                                Services
                            </Link>
                        </li>
                        <li>
                            <Link to="/ilo-platform" className="text-sm font-medium hover:text-[#E91E63] transition-colors">
                                ILO Platform
                            </Link>
                        </li>
                        <li>
                            <Link to="/therapeutic-areas" className="text-sm font-medium hover:text-[#E91E63] transition-colors">
                                Therapeutic Areas
                            </Link>
                        </li>
                        
                        {/* Resources Dropdown */}
                        <li className="relative">
                            <button 
                                onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                                className="flex items-center gap-1 text-sm font-medium hover:text-[#E91E63] transition-colors"
                            >
                                Resources
                                <ChevronDown className={`w-4 h-4 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {isResourcesOpen && (
                                <>
                                    {/* Backdrop to close dropdown when clicking outside */}
                                    <div 
                                        className="fixed inset-0 z-10" 
                                        onClick={() => setIsResourcesOpen(false)}
                                    ></div>
                                    
                                    <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-20">
                                        <Link 
                                            to="/news-kiosk" 
                                            className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 group"
                                            onClick={closeAllMenus}
                                        >
                                            <svg className="w-5 h-5 text-[#475569] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                                            </svg>
                                            <div>
                                                <div className="font-semibold text-[#E91E63] text-sm animate-pulse group-hover:text-[#C2185B]">News Kiosk</div>
                                                <div className="text-xs text-gray-500 mt-1">Regulatory updates & industry insights</div>
                                            </div>
                                        </Link>
                                        
                                        <Link 
                                            to="/resources" 
                                            className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors border-b border-gray-100"
                                            onClick={closeAllMenus}
                                        >
                                            <BookOpen className="w-5 h-5 text-[#475569] flex-shrink-0 mt-0.5" />
                                            <div>
                                                <div className="font-semibold text-gray-900 text-sm">Research Resources</div>
                                                <div className="text-xs text-gray-500 mt-1">FDA guidelines, IIT best practices & more</div>
                                            </div>
                                        </Link>
                                        
                                        <Link 
                                            to="/for-investigators" 
                                            className="flex items-start gap-3 p-4 hover:bg-gray-50 transition-colors"
                                            onClick={closeAllMenus}
                                        >
                                            <Users className="w-5 h-5 text-[#475569] flex-shrink-0 mt-0.5" />
                                            <div>
                                                <div className="font-semibold text-gray-900 text-sm">For Investigators</div>
                                                <div className="text-xs text-gray-500 mt-1">Partner with KLINISOL for your IIT</div>
                                            </div>
                                        </Link>
                                    </div>
                                </>
                            )}
                        </li>

                        <li>
                            <Link to="/about" className="text-sm font-medium hover:text-[#E91E63] transition-colors">
                                About
                            </Link>
                        </li>
                        <li>
                            <Link to="/contact" className="text-sm font-medium hover:text-[#E91E63] transition-colors">
                                Contact
                            </Link>
                        </li>

                        {/* Auth Section */}
                        {isAuthenticated ? (
                            <>
                                <li>
                                    <Link 
                                        to="/dashboard" 
                                        className="text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    >
                                        Dashboard
                                    </Link>
                                </li>
                                {user && (
                                    <li className="flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-md">
                                        <span className="text-xs font-medium">
                                            {user.firstName} {user.lastName}
                                        </span>
                                    </li>
                                )}
                                <li>
                                    <button
                                        onClick={handleLogout}
                                        className="w-11 h-11 rounded-full bg-gradient-to-br from-[#475569] to-[#E91E63] hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center group"
                                        title="Logout"
                                    >
                                        <PowerOff className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" />
                                    </button>
                                </li>
                            </>
                        ) : (
                            <li>
                                <Link
                                    to="/login"
                                    className="px-4 py-2 text-sm font-semibold bg-white/10 hover:bg-white/20 rounded-md transition-all"
                                >
                                    Login
                                </Link>
                            </li>
                        )}
                    </ul>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMenu}
                        className="lg:hidden p-2 hover:bg-white/10 rounded-md transition-all"
                        aria-label="Toggle menu"
                    >
                        {isMenuOpen ? (
                            <X className="w-6 h-6" />
                        ) : (
                            <Menu className="w-6 h-6" />
                        )}
                    </button>
                </div>

                {/* Mobile Navigation */}
                {isMenuOpen && (
                    <div className="lg:hidden mt-4 pt-4 border-t border-white/20">
                        <ul className="flex flex-col gap-3">
                            <li>
                                <Link 
                                    to="/" 
                                    className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    onClick={closeAllMenus}
                                >
                                    Home
                                </Link>
                            </li>
                            <li>
                                <Link 
                                    to="/services" 
                                    className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    onClick={closeAllMenus}
                                >
                                    Services
                                </Link>
                            </li>
                            <li>
                                <Link 
                                    to="/ilo-platform" 
                                    className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    onClick={closeAllMenus}
                                >
                                    ILO Platform
                                </Link>
                            </li>
                            <li>
                                <Link 
                                    to="/therapeutic-areas" 
                                    className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    onClick={closeAllMenus}
                                >
                                    Therapeutic Areas
                                </Link>
                            </li>

                            {/* Mobile Resources Submenu */}
                            <li>
                                <button 
                                    onClick={() => setIsResourcesOpen(!isResourcesOpen)}
                                    className="w-full flex items-center justify-between py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                >
                                    Resources
                                    <ChevronDown className={`w-4 h-4 transition-transform ${isResourcesOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {isResourcesOpen && (
                                    <ul className="pl-4 mt-2 space-y-2 border-l-2 border-white/20">
                                        <li>
                                            <Link 
                                                to="/resources" 
                                                className="block py-2 text-sm hover:text-[#E91E63] transition-colors"
                                                onClick={closeAllMenus}
                                            >
                                                Research Resources
                                            </Link>
                                        </li>
                                        <li>
                                            <Link 
                                                to="/for-investigators" 
                                                className="block py-2 text-sm hover:text-[#E91E63] transition-colors"
                                                onClick={closeAllMenus}
                                            >
                                                For Investigators
                                            </Link>
                                        </li>
                                        <li>
                                            <Link 
                                                to="/news-kiosk" 
                                                className="block py-2 text-sm hover:text-[#E91E63] transition-colors"
                                                onClick={closeAllMenus}
                                            >
                                                News Kiosk
                                            </Link>
                                        </li>
                                    </ul>
                                )}
                            </li>

                            <li>
                                <Link 
                                    to="/about" 
                                    className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    onClick={closeAllMenus}
                                >
                                    About
                                </Link>
                            </li>
                            <li>
                                <Link 
                                    to="/contact" 
                                    className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                    onClick={closeAllMenus}
                                >
                                    Contact
                                </Link>
                            </li>

                            {/* Mobile Auth Section */}
                            {isAuthenticated ? (
                                <>
                                    <li>
                                        <Link 
                                            to="/dashboard" 
                                            className="block py-2 text-sm font-medium hover:text-[#E91E63] transition-colors"
                                            onClick={closeAllMenus}
                                        >
                                            Dashboard
                                        </Link>
                                    </li>
                                    {user && (
                                        <li className="py-2 text-xs font-medium bg-white/10 rounded-md px-3">
                                            {user.firstName} {user.lastName} ({user.role})
                                        </li>
                                    )}
                                    <li>
                                        <button
                                            onClick={() => {
                                                handleLogout();
                                                closeAllMenus();
                                            }}
                                            className="w-full flex items-center justify-center gap-2 py-2 text-sm font-semibold bg-white/10 hover:bg-white/20 rounded-md transition-all"
                                        >
                                            <PowerOff className="w-4 h-4" />
                                            Logout
                                        </button>
                                    </li>
                                </>
                            ) : (
                                <li>
                                    <Link
                                        to="/login"
                                        className="block py-2 text-center text-sm font-semibold bg-white/10 hover:bg-white/20 rounded-md transition-all"
                                        onClick={closeAllMenus}
                                    >
                                        Login
                                    </Link>
                                </li>
                            )}
                        </ul>
                    </div>
                )}
            </div>
        </nav>
    );
};

export default Navbar;